import { useEffect, useRef } from 'react'
import { useUserStore } from '@/store/user'
import { subscribeToMercureTopic } from '@/lib/helpers/subscribeToMercureTopic.ts'
import createIri from '@/lib/helpers/createIri.ts'

type EventSubscriberOptions = {
  useCurrentOrganization?: boolean
  onRecovery?: () => void
}

function useEventSubscriber(
  topic: string,
  handler: (data: MessageEvent) => void,
  options: boolean | EventSubscriberOptions = false,
) {
  const currentOrganizationMembership = useUserStore(state => state.currentOrganizationMembership)
  const handlerRef = useRef(handler)
  const onRecovery = typeof options === 'boolean' ? undefined : options.onRecovery
  const useCurrentOrganization =
    typeof options === 'boolean' ? options : (options.useCurrentOrganization ?? false)
  const recoveryRef = useRef(onRecovery)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    recoveryRef.current = onRecovery
  }, [onRecovery])

  useEffect(() => {
    // Skip SSE subscription for invalid topics (e.g. public preview mode passes '/issues/null')
    if (topic.includes('/null')) return

    let composedTopic = topic
    if (useCurrentOrganization && currentOrganizationMembership) {
      const orgIri = createIri('organizations', currentOrganizationMembership.organization?.id)
      composedTopic = `${orgIri}${topic}`
    }

    const subscription = subscribeToMercureTopic(
      composedTopic,
      event => {
        handlerRef.current(event)
      },
      {
        onRecovery: () => recoveryRef.current?.(),
      },
    )

    return () => subscription.close()
  }, [topic, currentOrganizationMembership, useCurrentOrganization])
}

export default useEventSubscriber
